/**
 * Auftragsübersicht – reine Auswertung.
 *
 * Eine Sicht auf Angebote mit Status „Auftrag erteilt" oder höher, kein
 * eigener Datenbestand. Sie beantwortet drei Fragen und sonst keine: wie viel
 * wurde beauftragt und wie steht das zum Vorjahr, woher kommt das Volumen,
 * und was ist noch nicht abgerechnet. Ist-Erlöse und Zahlungseingänge bleiben
 * in den Finanzen – zwei Seiten, die dasselbe Geld zählen, widersprechen sich
 * irgendwann.
 *
 * Eine Ausnahme von „reine Auswertung" gibt es: den Auftragswert. Er ist die
 * Zahl, um die es hier geht, und wer eine Liste mit falschen Werten vor sich
 * hat, will sie dort richtigstellen, wo sie ihm auffällt – nicht in zwanzig
 * einzeln geöffneten Angeboten. Jede Änderung wird mit altem Wert, neuem
 * Wert, Person und Zeitpunkt in der Chronik des Angebots festgehalten.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { KoraMaskenhinweise } from '../komponenten/KoraHinweise';
import { IconStift } from '../komponenten/Icons';
import { Betragsfeld } from '../komponenten/Betragsfeld';
import { eur } from '../hilfen/format';
import type { Auftragsuebersicht } from '../typen';
import './auftraege.css';
import './auftraege-v2.css';
import { Menueknopf } from '../komponenten/Shell';

/*
 * Sortierbar sind nur zwei Spalten.
 *
 * Alles sortierbar zu machen klingt großzügig und wird nie benutzt: Nach
 * Kunde sortiert man mit der Suche, nach Gewerk mit dem Filter. Übrig bleiben
 * die beiden Fragen, die man wirklich stellt – wann und wie viel.
 */
type SortSpalte = 'datum' | 'wert';

export function Auftraege() {
  const navigate = useNavigate();
  const [jahr, setJahr] = useState('');
  const [suche, setSuche] = useState('');
  const [gewerk, setGewerk] = useState('');
  const [nurOhneWert, setNurOhneWert] = useState(false);
  const [sortSpalte, setSortSpalte] = useState<SortSpalte>('datum');
  const [absteigend, setAbsteigend] = useState(true);

  const queryClient = useQueryClient();
  /** Zeile, deren Auftragswert gerade bearbeitet wird – höchstens eine. */
  const [bearbeitet, setBearbeitet] = useState<number | null>(null);

  /**
   * Auftragswert ändern.
   *
   * Bewusst über dieselbe Route wie im Angebot: Die Auftragsübersicht bekommt
   * keinen eigenen Schreibweg. Sonst gäbe es zwei Stellen, an denen dieselbe
   * Zahl entsteht, und nur eine davon würde beim nächsten Umbau angepasst.
   */
  const wertMutation = useMutation({
    mutationFn: ({ id, auftrag }: { id: number; auftrag: number | null }) =>
      api.aendere(`/angebote/${id}/auftragswert`, { auftrag }),
    onSuccess: () => {
      setBearbeitet(null);
      // Auch die Summen, der Deckungsbeitrag und die Kategorien hängen daran.
      void queryClient.invalidateQueries({ queryKey: ['auftraege'] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
    },
    onError: () => setBearbeitet(null),
  });

  const abfrage = useQuery({
    queryKey: ['auftraege', jahr],
    queryFn: () =>
      api.hole<Auftragsuebersicht>(`/auftraege${jahr ? `?jahr=${encodeURIComponent(jahr)}` : ''}`),
  });

  const d = abfrage.data;

  /**
   * Jahreswechsel setzt alle Filter zurück.
   *
   * Ein Gewerk-Filter aus dem Vorjahr, der still weiterläuft, erzeugt eine
   * Summe, die niemand nachvollziehen kann – und man sieht ihm nicht an, dass
   * er noch aktiv ist, weil man gerade woanders hingeschaut hat.
   */
  function jahrWaehlen(j: string) {
    setJahr(j);
    setSuche('');
    setGewerk('');
    setNurOhneWert(false);
  }

  const gefiltert = useMemo(() => {
    let liste = d?.auftraege ?? [];
    if (gewerk) liste = liste.filter((a) => a.prefix === gewerk);
    if (nurOhneWert) liste = liste.filter((a) => a.ohneWert);

    const worte = suche.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (worte.length === 0) return liste;
    return liste.filter((a) => {
      const heuhaufen = [a.kennung, a.prefix, a.kategorie, a.name, a.kunde, a.ort]
        .join(' ')
        .toLowerCase();
      return worte.every((w) => heuhaufen.includes(w));
    });
  }, [d, suche, gewerk, nurOhneWert]);

  const liste = useMemo(() => {
    const richtung = absteigend ? -1 : 1;
    return [...gefiltert].sort((a, b) => {
      let v = sortSpalte === 'wert' ? a.wert - b.wert : a.datum.localeCompare(b.datum);
      if (v === 0) v = a.kennung.localeCompare(b.kennung, 'de-AT', { numeric: true });
      return v * richtung;
    });
  }, [gefiltert, sortSpalte, absteigend]);

  /**
   * Die Summe der gerade sichtbaren Zeilen.
   *
   * Ohne sie muss man die gefilterte Ansicht im Kopf addieren – und genau das
   * ist der häufigste Grund, warum Auswertungen nach Excel exportiert werden.
   */
  const sichtbar = useMemo(
    () => ({
      wert: liste.reduce((s, a) => s + a.wert, 0),
      beitrag: liste.reduce((s, a) => s + a.beitrag, 0),
      ohneWert: liste.filter((a) => a.ohneWert).length,
    }),
    [liste],
  );

  function sortiereNach(spalte: SortSpalte) {
    if (spalte === sortSpalte) {
      setAbsteigend((a) => !a);
      return;
    }
    setSortSpalte(spalte);
    setAbsteigend(true);
  }

  const sortState = { aktiv: sortSpalte, absteigend, onSortieren: sortiereNach };
  const gefiltertAktiv = !!gewerk || nurOhneWert || !!suche;
  const gewerkLabel = d?.nachKategorie.find((k) => k.prefix === gewerk)?.label ?? gewerk;

  return (
    <div className="eh-inhalt auf">
      <div className="eh-kopfzeile">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
          <div className="eh-kicker">Auswertung</div>
          <div className="eh-titel">Auftragsauswertung</div>
          <div className="auf-untertitel">
            Beauftragte Angebote mit Auftragswert und erwartetem Deckungsbeitrag. Der Auftragswert
            lässt sich hier über den Stift richtigstellen; jede Änderung steht mit Person und
            Zeitpunkt in der Chronik des Angebots.
          </div>
        </div>

        {/* Nachrichten-Symbol zuerst, dann die Bedienelemente der Maske. */}
        </div>
        <div className="eh-kopf-rechts">
          <Nachrichtenknopf />

        {d && d.jahre.length > 0 && (
          <div className="auf-jahrblock">
            {/*
              Ab sechs Jahren wird aus der Leiste ein Auswahlfeld. Eine Leiste
              mit zehn Knöpfen ist keine Leiste mehr, sondern eine Tapete.
            */}
            {d.jahre.length > 5 ? (
              <select
                className="auf-jahrwahl"
                value={d.jahr}
                onChange={(e) => jahrWaehlen(e.target.value)}
                aria-label="Jahr wählen"
              >
                {d.jahre.map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
            ) : (
              <div className="auf-jahre">
                {d.jahre.map((j) => (
                  <button
                    key={j}
                    type="button"
                    className={`auf-jahr ${j === d.jahr ? 'aktiv' : ''}`}
                    onClick={() => jahrWaehlen(j)}
                  >
                    {j}
                    {/* Der Punkt markiert das laufende Jahr – auch wenn man
                        gerade ein anderes ansieht. */}
                    {d.laufendesJahr && j === d.jahr && <span className="auf-jahr-punkt" />}
                  </button>
                ))}
              </div>
            )}
            <div className="auf-stand">{d.standText}</div>
          </div>
        )}
        </div>
      </div>

      {abfrage.isLoading && <div className="eh-karte">Aufträge werden geladen …</div>}

      {d && (
        <>
          <div className="auf-kpis">
            <div className="auf-kpi">
              <span className="auf-kpi-label">Auftragsvolumen</span>
              <span className="auf-kpi-zeile">
                <span className="auf-kpi-wert">{d.volumen.wertText}</span>
                {d.volumen.vergleich.vorhanden ? (
                  <span
                    className={`auf-pille auf-pille--${d.volumen.vergleich.richtung}`}
                    title={d.volumen.vergleich.basisText}
                  >
                    {d.volumen.vergleich.text}
                  </span>
                ) : (
                  <span className="auf-pille auf-pille--keine">{d.volumen.vergleich.text}</span>
                )}
              </span>
              <span className="auf-kpi-hinweis">{d.volumen.basisText}</span>
            </div>

            <div className="auf-kpi">
              <span className="auf-kpi-label">Erwarteter Deckungsbeitrag</span>
              <span className="auf-kpi-zeile">
                <span className="auf-kpi-wert auf-kpi-wert--geld">{d.deckungsbeitrag.wertText}</span>
                {d.deckungsbeitrag.margeText && (
                  <span className="auf-marge">{d.deckungsbeitrag.margeText}</span>
                )}
              </span>
              <span className="auf-kpi-hinweis">{d.deckungsbeitrag.hinweis}</span>
            </div>

            {/*
              Eine Kachel statt zwei: „offen" und „abgerechnet" waren zwei
              Kacheln für dieselbe Information. Der Balken zeigt das
              Verhältnis, die Zahlen darunter die Beträge.
            */}
            <div className="auf-kpi">
              <span className="auf-kpi-label">Abrechnungsstand</span>
              <div
                className="auf-abr-schiene"
                title={`${d.abrechnung.anteilFertig} % des Volumens abgerechnet`}
              >
                <div className="auf-abr-fertig" style={{ width: `${d.abrechnung.anteilFertig}%` }} />
              </div>
              <div className="auf-abr-werte">
                <span>
                  <span className="auf-abr-punkt auf-abr-punkt--fertig" />
                  {d.abrechnung.fertigText}
                  <em>abgerechnet · {d.abrechnung.fertigAnzahl}</em>
                </span>
                <span>
                  <span className="auf-abr-punkt" />
                  {d.abrechnung.offenText}
                  <em>offen · {d.abrechnung.offenAnzahl}</em>
                </span>
              </div>
            </div>
          </div>

          {d.ohneWert > 0 && (
            <div className="auf-warnung">
              <span>
                {d.ohneWert} {d.ohneWert === 1 ? 'Auftrag hat' : 'Aufträge haben'} keinen
                Auftragswert – {d.ohneWert === 1 ? 'er zählt' : 'sie zählen'} in keiner Summe dieser
                Seite mit.
              </span>
              <button
                type="button"
                className="auf-warnknopf"
                onClick={() => setNurOhneWert((n) => !n)}
              >
                {nurOhneWert ? 'Filter aufheben' : `Die ${d.ohneWert} anzeigen`}
              </button>
            </div>
          )}

          {d.verlaufMax > 0 && (
            <div className="auf-karte">
              <div className="auf-label">Jahresverlauf · {d.jahr}</div>
              <Jahresverlauf daten={d} />
            </div>
          )}

          {d.nachKategorie.length > 0 && (
            <div className="auf-karte">
              <div className="auf-label">Volumen je Gewerk · {d.jahr}</div>
              <div className="auf-katliste">
                {d.nachKategorie.map((k) => (
                  <button
                    type="button"
                    className={`auf-kat ${gewerk === k.prefix ? 'aktiv' : ''}`}
                    key={k.prefix}
                    onClick={() => setGewerk(gewerk === k.prefix ? '' : k.prefix)}
                    title={
                      `Tabelle auf ${k.label} filtern` +
                      (k.ohneAufschlag
                        ? ' · kein Aufschlag hinterlegt'
                        : ` · ${k.aufschlag} % Aufschlag hinterlegt`)
                    }
                  >
                    <div className="auf-kat-kopf">
                      <span className="auf-kat-name">
                        <span className="auf-kat-punkt" style={{ background: k.farbe }} />
                        {k.label}
                        <span className="auf-kat-anzahl">
                          {k.anzahl} {k.anzahl === 1 ? 'Auftrag' : 'Aufträge'}
                        </span>
                      </span>
                      <span className="auf-kat-werte">
                        <strong>{k.wertText}</strong>
                        <span className="auf-kat-beitrag">
                          {k.ohneAufschlag ? 'kein Aufschlag' : `${k.beitragText} DB`}
                        </span>
                      </span>
                    </div>
                    <div className="auf-kat-schiene">
                      <div
                        className="auf-kat-fuellung"
                        style={{ width: `${k.anteil}%`, background: k.farbe }}
                      />
                      {/* Die dünne Linie darunter ist das Vorjahr, auf
                          derselben Skala – sonst wäre sie Dekoration. */}
                      {d.volumen.wert > 0 && k.vorjahrWert > 0 && (
                        <div
                          className="auf-kat-vorjahr"
                          style={{ width: `${Math.min(100, (k.vorjahrWert / d.volumen.wert) * 100)}%` }}
                          title={`Vorjahr: ${k.vorjahrText}`}
                        />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="eh-kopfzeile">
            <div>
              <div className="auf-listentitel">Aufträge {d.jahr}</div>
              <div className="auf-listenhinweis">
                Datum ist der Tag der Beauftragung, nicht der Angebotslegung
              </div>
            </div>
            <label className="auf-suche">
              <span aria-hidden="true">⌕</span>
              <input
                type="search"
                value={suche}
                placeholder="Suchen: Nummer, Kunde, Gewerk …"
                aria-label="Aufträge durchsuchen"
                onChange={(e) => setSuche(e.target.value)}
              />
              {suche && (
                <button type="button" onClick={() => setSuche('')} title="Suche zurücksetzen">
                  ×
                </button>
              )}
            </label>
          </div>

          {/* Aktive Filter stehen sichtbar über der Tabelle und lassen sich
              dort abwählen – ein Filter, den man nicht sieht, ist eine Falle. */}
          {gefiltertAktiv && (
            <div className="auf-filterzeile">
              {gewerk && (
                <button type="button" className="auf-filter" onClick={() => setGewerk('')}>
                  Gewerk: {gewerkLabel} <span aria-hidden="true">×</span>
                </button>
              )}
              {nurOhneWert && (
                <button type="button" className="auf-filter" onClick={() => setNurOhneWert(false)}>
                  ohne Auftragswert <span aria-hidden="true">×</span>
                </button>
              )}
              {suche && (
                <button type="button" className="auf-filter" onClick={() => setSuche('')}>
                  Suche: {suche} <span aria-hidden="true">×</span>
                </button>
              )}
            </div>
          )}

          <div className="auf-tabelle-rahmen">
            <div className="auf-tabelle">
              <div className="auf-zeile auf-kopf">
                <div className="auf-sp-fest">Auftrag</div>
                <div className="auf-sp-fest">Gewerk</div>
                <div className="auf-sp-fest">Kunde</div>
                <Kopf spalte="datum" {...sortState}>
                  Beauftragt
                </Kopf>
                <Kopf spalte="wert" rechts stiftplatz {...sortState}>
                  Auftragswert
                </Kopf>
                <div className="auf-sp-fest auf-rechts">Deckungsbeitrag</div>
                <div className="auf-sp-fest">Abrechnung</div>
              </div>

              {liste.map((a) => (
                <div
                  className={`auf-zeile auf-datenzeile ${a.ohneWert ? 'auf-zeile--luecke' : ''}`}
                  key={a.id}
                >
                  <button
                    type="button"
                    className="auf-name"
                    onClick={() =>
                      navigate(`/angebot/${a.id}`, { state: { herkunft: 'auftraege' } })
                    }
                    title={`${a.name} – im Angebot öffnen`}
                  >
                    {a.kennung}_{a.prefix}
                  </button>

                  <div className="auf-gewerk" title={a.kategorie}>
                    <span
                      className="auf-kat-punkt"
                      style={{
                        background:
                          d.nachKategorie.find((k) => k.prefix === a.prefix)?.farbe ??
                          'var(--eh-text-5)',
                      }}
                    />
                    {a.kategorie}
                  </div>

                  <div className="auf-kunde" title={a.ort}>
                    {a.kunde}
                  </div>
                  <div className="auf-datum">{a.datumText || '—'}</div>

                  <div className="auf-wert">
                    {bearbeitet === a.id ? (
                      <Betragsfeld
                        wert={a.ohneWert ? null : a.wert}
                        autoFokus
                        laeuft={wertMutation.isPending}
                        onSpeichern={(neu) => wertMutation.mutate({ id: a.id, auftrag: neu })}
                        onAbbrechen={() => setBearbeitet(null)}
                      />
                    ) : a.ohneWert ? (
                      <button
                        type="button"
                        className="auf-wert-fehlt"
                        title="Auftragswert nachtragen"
                        onClick={() => setBearbeitet(a.id)}
                      >
                        + Wert
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="auf-wert-knopf"
                        title="Auftragswert ändern"
                        onClick={() => setBearbeitet(a.id)}
                      >
                        {a.wertText}
                        <span className="auf-stift">
                          <IconStift groesse={13} />
                        </span>
                      </button>
                    )}
                  </div>

                  <div
                    className="auf-wert auf-beitrag auf-rechts"
                    title={a.margeText ? `Marge ${a.margeText}` : ''}
                  >
                    {a.ohneAufschlag ? <span className="auf-fehlt">—</span> : a.beitragText}
                  </div>

                  <div className="auf-abr">
                    <span className={`auf-abr-pille ${a.abgerechnet ? 'fertig' : ''}`}>
                      {a.abgerechnet ? 'abgerechnet' : 'offen'}
                    </span>
                  </div>
                </div>
              ))}

              {liste.length === 0 && (
                <div className="auf-leer">
                  {gefiltertAktiv
                    ? `Kein Treffer unter ${d.auftraege.length} Aufträgen.`
                    : `Für ${d.jahr} ist noch kein Auftrag erfasst.`}
                </div>
              )}

              {liste.length > 0 && (
                <div className="auf-zeile auf-summenzeile">
                  <div className="auf-sp-fest auf-summe-basis">
                    {liste.length === d.auftraege.length
                      ? `${liste.length} ${liste.length === 1 ? 'Auftrag' : 'Aufträge'}`
                      : `${liste.length} von ${d.auftraege.length} Aufträgen`}
                  </div>
                  <div className="auf-sp-fest" />
                  <div className="auf-sp-fest" />
                  <div className="auf-sp-fest" />
                  <div className="auf-summe-wert auf-rechts auf-stiftplatz">
                    {eur(sichtbar.wert)}
                    {sichtbar.ohneWert > 0 && (
                      <em title={`${sichtbar.ohneWert} ohne Wert`}>
                        {' '}
                        · {sichtbar.ohneWert} ohne Wert
                      </em>
                    )}
                  </div>
                  <div className="auf-summe-wert auf-rechts">{eur(sichtbar.beitrag)}</div>
                  <div className="auf-sp-fest" />
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {/* KORA steht unter dem Inhalt der Maske, nicht darüber (v11, 10.2 Regel 6). */}
      <KoraMaskenhinweise maske="auftragsauswertung" label="Auftragsauswertung" />
    </div>
  );
}

/**
 * Jahresverlauf über zwölf Monate.
 *
 * Der breite blasse Balken dahinter ist das Vorjahr, auf demselben Maßstab.
 * Zwei Skalen nebeneinander sind die häufigste Lüge in Auswertungen, deshalb
 * steht das Maximum auch im Klartext unter dem Diagramm – ohne diese Zahl
 * sind Balkenhöhen Dekoration.
 */
function Jahresverlauf({ daten }: { daten: Auftragsuebersicht }) {
  const vorjahr = String(Number(daten.jahr) - 1);
  return (
    <div className="auf-verlauf">
      <div className="auf-verlauf-balken">
        {daten.verlauf.map((m) => {
          const h = (w: number) => (daten.verlaufMax ? Math.round((w / daten.verlaufMax) * 100) : 0);
          return (
            <div
              className={`auf-monat ${m.zukunft ? 'zukunft' : ''} ${m.laufend ? 'laufend' : ''}`}
              key={m.monat}
              title={
                m.zukunft
                  ? `${m.label} ${daten.jahr}: noch nicht gewesen`
                  : `${m.label} ${daten.jahr}: ${eur(m.wert)} · ${m.label} ${vorjahr}: ${eur(m.vorjahr)}`
              }
            >
              <div className="auf-monat-saeule">
                <div className="auf-monat-vorjahr" style={{ height: `${h(m.vorjahr)}%` }} />
                {/* Zukünftige Monate bleiben leer statt auf null zu fallen –
                    sonst liest man einen Einbruch, wo nur Zukunft ist. */}
                {!m.zukunft && (
                  <div className="auf-monat-jahr" style={{ height: `${h(m.wert)}%` }} />
                )}
              </div>
              <div className="auf-monat-label">{m.label}</div>
            </div>
          );
        })}
      </div>
      <div className="auf-verlauf-fuss">
        <span className="auf-verlauf-legende">
          <span className="auf-legende-punkt auf-legende-punkt--jahr" /> {daten.jahr}
          <span className="auf-legende-punkt auf-legende-punkt--vorjahr" /> {vorjahr}
        </span>
        <span className="auf-verlauf-massstab">
          höchster Monat: {daten.verlaufMaxText}
        </span>
      </div>
    </div>
  );
}

/**
 * Klickbarer Spaltenkopf – nur für die beiden sortierbaren Spalten.
 *
 * `rechts` für Zahlenspalten, `stiftplatz` zusätzlich dort, wo hinter der
 * Zahl noch ein Stift steht: Sonst endet die Überschrift am Zellenrand und
 * die Zahlen darunter 21 px davor – ein Versatz, den man sieht, ohne ihn
 * benennen zu können.
 */
function Kopf({
  spalte,
  aktiv,
  absteigend,
  onSortieren,
  rechts = false,
  stiftplatz = false,
  children,
}: {
  spalte: SortSpalte;
  aktiv: SortSpalte;
  absteigend: boolean;
  onSortieren: (s: SortSpalte) => void;
  rechts?: boolean;
  stiftplatz?: boolean;
  children: React.ReactNode;
}) {
  const istAktiv = aktiv === spalte;
  return (
    <button
      type="button"
      className={`auf-kopfknopf ${istAktiv ? 'aktiv' : ''} ${rechts ? 'auf-rechts' : ''} ${
        stiftplatz ? 'auf-stiftplatz' : ''
      }`}
      onClick={() => onSortieren(spalte)}
      aria-sort={istAktiv ? (absteigend ? 'descending' : 'ascending') : 'none'}
      title={istAktiv ? 'Richtung umkehren' : 'Nach dieser Spalte sortieren'}
    >
      <span className="auf-kopf-text">{children}</span>
      <span className="auf-pfeil" aria-hidden="true">
        {istAktiv ? (absteigend ? '▾' : '▴') : ''}
      </span>
    </button>
  );
}
